import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { AlertTriangleIcon, RefreshCwIcon, XCircleIcon } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { useContent, useDateTime, useLabel, useT } from '@/lib/i18n';
import { notify } from '@/lib/notify';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useModuleName } from '@/hooks/use-module-name';
import { useCannotBecause } from '@/lib/permissions';
import { useUrlState } from '@/lib/url-state';
import { VIZ, TableViz } from '@/features/result/viz';
import { NarrativeCard, PathwaysCard } from '@/features/result/interpretation';
import { useHeadline } from '@/features/result/headline';
import { PrecisionCard } from '@/features/result/precision';

/**
 * Result view — rendered BY `envelope.visualization` (build/05 §G2, PRD FR-R1).
 *
 * The whole screen contains no model name and no branch on one. It looks up
 * `VIZ[envelope.visualization]`, falls back to the table for a hint it does not
 * recognise, and draws the `display` projection — so a fourth psychometric model
 * renders here on the day it is registered, with no edit to this file.
 *
 * The envelope is read VERBATIM and never recomputed (build/02 §1). A screen
 * that recalculated a band from the score would let a config change silently
 * rewrite a result that was finalised months ago.
 */
/**
 * TWO READS OF ONE ENVELOPE (Workstream N1).
 *
 * The screen used to be a single column that opened with a chart and closed with
 * seven version stamps. Both readers were served badly by that: the counsellor
 * scrolled past reproducibility metadata to reach the end of the report, and the
 * psychometrician auditing a re-score had to scroll past the report to reach the
 * metadata — and could not send anyone a link to it.
 *
 * ### Why tabs, and not two stacked sections or a disclosure
 *
 * The requirement is asymmetric in a way that decides it. Interpretation must be
 * reached by doing NOTHING, and provenance must be reached in ONE action and be
 * LINKABLE. Stacking keeps both on screen but makes provenance a scroll whose
 * length depends on how many dimensions the model has — no action, but no
 * address either. A disclosure is one action and hides its content from find-in-
 * page. Tabs give the counsellor the default read for free, put the technical
 * read one click away, and — with `useUrlState` — give it a URL: `?view=technical`
 * is what a reviewer pastes to a colleague, which is exactly the case T3 put
 * list state in the URL for. Tabs are also already the console's mechanism for
 * this (Settings, Profile, the scoring-config editor); a fourth pattern here
 * would be a new thing to learn for no gain.
 *
 * ### What is deliberately OUTSIDE the tabs
 *
 * The headline, the module, the candidate, the score type, Re-score — and the
 * **QC banner**. A run whose score is being withheld pending review must not be
 * readable as a finished report because someone happened to be on the other tab.
 * A warning behind a tab is a warning that can be missed, and this one is the
 * reason the score is not to be trusted yet.
 *
 * Nothing was removed: every card this screen drew before is still here, in one
 * read or the other.
 */
/** The two reads. `interpretation` is the default and therefore the bare URL. */
const TECHNICAL = 'technical';
const INTERPRETATION = 'interpretation';
/**
 * Re-score against the module's CURRENT config (FR-P7).
 *
 * Writes a new result and navigates to it; the one being viewed is retained and
 * stays reachable, because results are append-only. The new envelope names the
 * old one in `supersedes`, which the Provenance card below shows — so the
 * lineage is visible rather than implied.
 */
function RescoreButton({ resultId }: { resultId: string }) {
  const navigate = useNavigate();
  const t = useT();
  // Re-scoring writes a new result. An analyst reading a report may not, and
  // finding that out by pressing the button is not a design.
  const cannot = useCannotBecause('assessments:run');

  const rescore = useMutation({
    mutationFn: () => api.rescoreResult(resultId),
    onSuccess: (envelope) => {
      notify.success(
        t('result.rescored'),
        t('result.rescored.detail', {
          result: envelope.resultId,
          version: envelope.configVersion,
        }),
      );
      navigate(`/results/${envelope.resultId}`);
    },
    onError: (err) =>
      notify.error(
        t('result.rescoreFailed'),
        err instanceof ApiError ? err.message : t('table.errorDescription'),
      ),
  });

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => rescore.mutate()}
      disabled={rescore.isPending || cannot !== null}
      title={cannot ?? undefined}
    >
      <RefreshCwIcon className="size-4" />
      {rescore.isPending ? t('result.rescoring') : t('result.rescore')}
    </Button>
  );
}

export function Result() {
  const { resultId = '' } = useParams();
  const t = useT();
  const content = useContent();
  const label = useLabel();
  const moduleName = useModuleName();
  // X4: this card printed a raw ISO string while every other date on the
  // platform goes through `useDateTime`. In an Arabic report it was unreadable.
  const dateTime = useDateTime();
  // N5. The headline was the last string on this screen that was English
  // whatever the operator reads — and the largest one, since it is the page
  // title. `useHeadline` resolves `headline.key` + `vars` against the catalogue
  // and falls back to the English sentence, never to a key.
  const headline = useHeadline();
  // The selected read lives in the URL, so the technical view of a result is a
  // link rather than a set of instructions.
  const [{ view }, setView] = useUrlState({ view: INTERPRETATION });
  const { data, isLoading, error } = useQuery({
    queryKey: ['result', resultId],
    queryFn: () => api.result(resultId),
    enabled: Boolean(resultId),
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl">
        <EmptyState
          icon={XCircleIcon}
          title={t('result.loadFailed')}
          description={error instanceof Error ? error.message : t('table.errorDescription')}
        />
      </div>
    );
  }

  if (!data) return null;

  const { display } = data.score;
  // Unknown hint → the table, which can draw any display projection. Never a
  // blank panel, and never a guess based on the model.
  const Viz = VIZ[data.visualization] ?? TableViz;
  const narrativeKeys = data.score.narrativeKeys ?? [];
  // Resolved ONCE and passed to every call site. The page title and the chart's
  // description are the same sentence, and resolving it twice is how they end up
  // being two sentences after someone edits one of them.
  const headlineText = headline(display.headline, display.dimensions);
  // A `?view=` this screen does not have lands on the default rather than on a
  // blank panel — the same rule the viz lookup follows one line above.
  const read = view === TECHNICAL ? TECHNICAL : INTERPRETATION;

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        eyebrow={t('result.eyebrow')}
        title={headlineText}
        description={`${data.candidateRef} · ${moduleName(data.moduleId)}`}
        actions={
          <>
            {/* X5: `visualization` was a badge here. `radar` / `bars` is the hint
                that picks a RENDERER — our vocabulary, not a fact about the
                person being reported on. The score TYPE stays: it names the
                instrument, which is something a reader needs. */}
            <Badge variant="secondary" className="font-mono">{data.score.type}</Badge>
            <RescoreButton resultId={data.resultId} />
          </>
        }
      />

      {data.qcFlags.length > 0 && (
        <div
          className="border-destructive/40 bg-destructive/5 mb-4 flex flex-wrap items-center gap-2 rounded-md border p-3"
          role="status"
        >
          <AlertTriangleIcon className="text-destructive size-4" />
          <span className="text-sm font-medium">{t('result.qcFlags')}</span>
          {data.qcFlags.map((flag) => (
            // Status is never colour alone — the flag name is always the label.
            <Badge key={flag} variant="destructive">{label('qcFlag', flag)}</Badge>
          ))}
          <span className="text-muted-foreground text-sm">{t('result.qcFlags.hint')}</span>
        </div>
      )}

      <Tabs value={read} onValueChange={(next) => setView({ view: next })}>
        <TabsList aria-label={t('result.reads')}>
          <TabsTrigger value={INTERPRETATION}>{t('result.tab.interpretation')}</TabsTrigger>
          <TabsTrigger value={TECHNICAL}>{t('result.tab.technical')}</TabsTrigger>
        </TabsList>

        {/* ------------------------------------------------ the counsellor's read */}
        <TabsContent value={INTERPRETATION} className="mt-4">
          <Card className="mb-4">
            <CardHeader>
              {/* The headline's LABEL is bilingual content from the envelope — the
                  analyst's language, like every other label on this report. Its
                  VALUE is the sentence, and N5 is what made it readable in that
                  same language: `headline.key` + `vars` resolved here, the
                  English `value` if this console has never seen the key. */}
              <CardTitle className="text-base">{content(display.headline.label)}</CardTitle>
              <CardDescription>{headlineText}</CardDescription>
            </CardHeader>
            <CardContent>
              <Viz
                headline={headlineText}
                dimensions={display.dimensions}
                normGroup={display.normGroup}
              />
            </CardContent>
          </Card>

          {/* N2. Both render NOTHING when their field is empty, which is every
              real result today — the plugins hard-code both empty and say why.
              Neither asks which model produced the block. */}
          <NarrativeCard
            narrativeKeys={data.score.narrativeKeys}
            dimensions={display.dimensions}
          />

          {/* The table view of every chart — the accessible reading, always
              present. Compared against the RENDERER rather than against the
              hint: an unrecognised hint already falls back to the table above,
              and `data.visualization !== 'table'` then drew the same table
              twice. */}
          {Viz !== TableViz && (
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-base">{t('result.allDimensions')}</CardTitle>
                <CardDescription>{t('result.allDimensions.hint')}</CardDescription>
              </CardHeader>
              <CardContent>
                {/* The accessible reading, and — since neither the radar nor the
                    gauge draws one — the only place the interval and the
                    percentile are guaranteed to appear. `normGroup` travels with
                    them: a percentile with no cohort named answers nothing. */}
                <TableViz
                  headline={headlineText}
                  dimensions={display.dimensions}
                  normGroup={display.normGroup}
                />
              </CardContent>
            </Card>
          )}

          <PathwaysCard pathways={data.score.recommendedPathways} />
        </TabsContent>

        {/* ------------------------------------------- the psychometrician's read */}
        {/* The provenance card, unchanged, plus the envelope fields that are
            metadata rather than report. The response pattern and per-item timing
            belong here too and are not built yet — they are a separate piece of
            work with the QC reviewer as their first reader, and stubbing a panel
            for them now would promise a view nobody can open. */}
        <TabsContent value={TECHNICAL} className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('result.provenance')}</CardTitle>
              <CardDescription>{t('result.provenance.hint')}</CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 gap-y-2 text-sm sm:grid-cols-3">
                <dt className="text-muted-foreground">{t('result.field.result')}</dt>
                <dd className="font-mono text-xs sm:col-span-2" dir="ltr">{data.resultId}</dd>

                <dt className="text-muted-foreground">{t('result.field.config')}</dt>
                <dd className="sm:col-span-2">v{data.configVersion}</dd>

                <dt className="text-muted-foreground">{t('result.field.instrument')}</dt>
                <dd className="sm:col-span-2" dir="ltr">{data.instrumentVersion}</dd>

                {/* THE REPRODUCIBILITY FACT, and the reason this screen does not
                    otherwise consult it: the language a run was administered in is
                    a property of the run, reported here, not a display setting for
                    the analyst reading the report. */}
                <dt className="text-muted-foreground">{t('result.field.administeredIn')}</dt>
                <dd className="sm:col-span-2">
                  {label('languageName', data.administeredLocale)}
                  <span className="text-muted-foreground ms-2 font-mono text-xs" dir="ltr">
                    {data.administeredLocale}
                  </span>
                </dd>

                <dt className="text-muted-foreground">{t('result.field.esco')}</dt>
                <dd className="sm:col-span-2" dir="ltr">{data.escoEdition}</dd>

                {/* `font-mono` belongs to the ID, not to the sentence that stands in
                    for it. Monospace faces have no Arabic shaping, so "أول تقدير"
                    rendered with its letters disconnected — S6 caught it in the
                    first Arabic screenshot of this card. An id is also LTR content
                    and says so, the way `resultId` above does. */}
                <dt className="text-muted-foreground">{t('result.field.supersedes')}</dt>
                {data.supersedes ? (
                  <dd className="font-mono text-xs sm:col-span-2" dir="ltr">{data.supersedes}</dd>
                ) : (
                  <dd className="text-muted-foreground sm:col-span-2">{t('result.firstScoring')}</dd>
                )}

                <dt className="text-muted-foreground">{t('result.field.finalised')}</dt>
                <dd className="sm:col-span-2">{dateTime(data.finalizedAt)}</dd>

                {/* The keys VERBATIM, and only when the envelope carries some.
                    The interpretation read drops any key it cannot turn into a
                    sentence — a slug shouted at a counsellor is worse than
                    silence — so this is where nothing is lost: to a
                    psychometrician a raw key IS the fact, being what the
                    instrument emitted and what a wording bank must answer. */}
                {narrativeKeys.length > 0 && (
                  <>
                    <dt className="text-muted-foreground">{t('result.field.narrativeKeys')}</dt>
                    <dd className="font-mono text-xs sm:col-span-2" dir="ltr">
                      {narrativeKeys.join(', ')}
                    </dd>
                  </>
                )}
              </dl>
            </CardContent>
          </Card>

          {/* N4 — SE(θ), and ONLY here. It is on the logit scale, the bars are
              on a normalised 0–100 one, and the two estimators are unrelated;
              the card names its own scale so nothing on this screen reads as a
              tolerance on a bar. Renders nothing unless the envelope carries
              one. */}
          <PrecisionCard skills={data.score.skills} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
